import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { useAppDispatch } from "app/hooks";
import { neutral } from "app/themes/colors";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  setContent,
  setOnChange,
  setOpen as setConfirmOpen,
  setOptions,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import {
  AMBIENT_SCRIBE_STATUS_COMPLETED,
  getScribeModalNestedDialogZIndex,
  getScribeModalPopoverZIndex
} from "features/medical-scribe/medical-scribe-constants";
import { formatAudioDurationMmSs } from "features/medical-scribe/medical-scribes-section/scribes-list/utils";
import {
  useDeleteScribeAudioChunkMutation,
  useDeleteScribeRecordingSessionMutation,
  useEditAmbientScribeMutation,
  useGetScribeAudioChunksQuery
} from "services/ambient-scribe";
import type { ScribeAudioChunk } from "services/types/ambient-scribe.type";

import {
  canDeleteAudioRecording,
  DELETE_CONFIRM_COMPLETED_EXTRA,
  DELETE_CONFIRM_IRREVERSIBLE,
  getAudioChunkText,
  getDeleteDisabledReason
} from "./recording-sessions-table.util";

type Props = {
  scribeId: number;
  scribeStatus: number | undefined;
  onDeleteInFlightChange?: (inFlight: boolean) => void;
};

type SessionRowProps = {
  duration: number;
  previewText: string;
  fullText: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  isDeleteDisabled: boolean;
  isDeleting: boolean;
  deleteDisabledReason: string | undefined;
  onDelete: () => void;
  tooltipPopperProps: { style: { zIndex: number } };
};

function getDeleteRecordingConfirmContent(needsEditBeforeDelete: boolean) {
  return (
    <Box>
      <Typography component="div" variant="body2">
        {DELETE_CONFIRM_IRREVERSIBLE}
      </Typography>
      {needsEditBeforeDelete && (
        <Typography component="div" variant="body2" sx={{ mt: 1.5 }}>
          {DELETE_CONFIRM_COMPLETED_EXTRA}
        </Typography>
      )}
    </Box>
  );
}

const cellBorder = { borderBottom: `1px solid ${neutral[300]}` };

function SessionRow({
  duration,
  previewText,
  fullText,
  isExpanded,
  onToggleExpand,
  isDeleteDisabled,
  isDeleting,
  deleteDisabledReason,
  onDelete,
  tooltipPopperProps
}: SessionRowProps) {
  const hasTranscript = Boolean(fullText);

  return (
    <>
      <TableRow>
        <TableCell
          sx={{
            ...cellBorder,
            verticalAlign: "top",
            width: 48,
            py: 1.5
          }}
        >
          <Tooltip
            title={isExpanded ? "Collapse" : "Expand"}
            disableInteractive
            PopperProps={tooltipPopperProps}
          >
            <IconButton
              size="small"
              aria-expanded={isExpanded}
              sx={{
                border: `solid 1px ${neutral[600]}`,
                width: 28,
                height: 28
              }}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand();
              }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell
          sx={{
            ...cellBorder,
            verticalAlign: "top",
            width: 120,
            py: 1.5,
            fontWeight: 700,
            whiteSpace: "nowrap"
          }}
        >
          Audio ({formatAudioDurationMmSs(duration)})
        </TableCell>
        <TableCell
          sx={{
            ...cellBorder,
            verticalAlign: "top",
            py: 1.5
          }}
        >
          {isExpanded ? (
            <Typography variant="body2" color="text.secondary">
              Transcription below
            </Typography>
          ) : (
            <Typography
              variant="body2"
              lineHeight={1.6}
              color={hasTranscript ? "text.primary" : "text.secondary"}
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              }}
            >
              {previewText || "No transcript for this session yet."}
            </Typography>
          )}
        </TableCell>
        <TableCell
          align="right"
          sx={{
            ...cellBorder,
            verticalAlign: "top",
            width: 48,
            py: 1.5
          }}
        >
          <Tooltip
            title={deleteDisabledReason ?? "Delete audio recording"}
            disableInteractive
            PopperProps={tooltipPopperProps}
          >
            <span>
              <IconButton
                size="small"
                disabled={isDeleteDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                color="error"
              >
                {isDeleting ? (
                  <CircularProgress size={14} />
                ) : (
                  <DeleteIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell sx={{ py: 0, borderBottom: "none" }} />
          <TableCell
            colSpan={3}
            sx={{
              ...cellBorder,
              py: 1.5,
              verticalAlign: "top"
            }}
          >
            <Typography
              variant="caption"
              component="div"
              fontWeight={700}
              color="text.secondary"
              sx={{ mb: 0.5, textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Transcription
            </Typography>
            <Typography
              variant="body2"
              lineHeight={1.6}
              color={hasTranscript ? "text.primary" : "text.secondary"}
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {fullText || "No transcript for this session yet."}
            </Typography>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function RecordingSessionsTable({
  scribeId,
  scribeStatus,
  onDeleteInFlightChange
}: Props) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const deleteOpRef = useRef(0);

  const tooltipPopperProps = useMemo(
    () => ({
      style: { zIndex: getScribeModalPopoverZIndex(theme.zIndex.snackbar) }
    }),
    [theme.zIndex.snackbar]
  );

  const { data: chunks = [], isLoading } = useGetScribeAudioChunksQuery(
    { scribeId },
    { skip: !scribeId }
  );
  const [deleteAudioChunk] = useDeleteScribeAudioChunkMutation();
  const [deleteSession] = useDeleteScribeRecordingSessionMutation();
  const [editScribe] = useEditAmbientScribeMutation();

  const isAnyDeleteInFlight = deletingId !== null;

  useEffect(() => {
    onDeleteInFlightChange?.(isAnyDeleteInFlight);
  }, [isAnyDeleteInFlight, onDeleteInFlightChange]);

  useEffect(
    () => () => onDeleteInFlightChange?.(false),
    [onDeleteInFlightChange]
  );

  const clearDeleteInFlight = useCallback(
    (opId: number) => {
      if (deleteOpRef.current === opId) {
        setDeletingId(null);
      }
    },
    []
  );

  const toggleExpanded = useCallback((id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleDeleteClick = useCallback(
    (chunk: ScribeAudioChunk) => {
      const needsEditBeforeDelete =
        scribeStatus === AMBIENT_SCRIBE_STATUS_COMPLETED;

      dispatch(setTitle("Delete this audio recording?"));
      dispatch(setContent(getDeleteRecordingConfirmContent(needsEditBeforeDelete)));
      dispatch(
        setOnChange(async () => {
          const opId = ++deleteOpRef.current;
          setDeletingId(chunk.recording_session_id);
          try {
            if (needsEditBeforeDelete) {
              try {
                await editScribe({ scribe_id: scribeId }).unwrap();
              } catch (err: any) {
                const msg =
                  err?.data?.error ??
                  err?.data?.detail ??
                  "Failed to update scribe before deleting. Please try again.";
                dispatch(setCustomMessage(msg));
                dispatch(setOpen(4000));
                return;
              }
            }
            if (chunk.id !== null) {
              await deleteAudioChunk({
                scribeId,
                chunkId: chunk.id
              }).unwrap();
            } else {
              await deleteSession({
                scribeId,
                sessionId: chunk.recording_session_id
              }).unwrap();
            }
          } catch (err: any) {
            const msg =
              err?.data?.error ??
              err?.data?.detail ??
              "Could not delete recording. Please try again.";
            dispatch(setCustomMessage(msg));
            dispatch(setOpen(4000));
          } finally {
            clearDeleteInFlight(opId);
          }
        })
      );
      dispatch(
        setOptions({
          zIndex: getScribeModalNestedDialogZIndex(theme.zIndex.snackbar),
          actionButtonType: "danger",
          actionButtonText: "Delete"
        })
      );
      dispatch(setConfirmOpen(true));
    },
    [
      scribeStatus,
      scribeId,
      dispatch,
      editScribe,
      deleteAudioChunk,
      deleteSession,
      clearDeleteInFlight,
      theme.zIndex.snackbar
    ]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!chunks.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No audio recordings found.
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableBody>
        {chunks.map((chunk) => {
          const canDelete = canDeleteAudioRecording(scribeStatus, chunks.length);
          const deleteDisabledReason = getDeleteDisabledReason(
            scribeStatus,
            chunks.length
          );
          const isRowDeleting = deletingId === chunk.recording_session_id;
          const isDeleteDisabled =
            !canDelete || isAnyDeleteInFlight;

          const allText = getAudioChunkText(chunk.transcription_chunks);
          const duration =
            chunk.duration_seconds ?? chunk.recording_session?.duration ?? 0;

          return (
            <SessionRow
              key={chunk.recording_session_id}
              duration={duration}
              previewText={allText.slice(0, 128)}
              fullText={allText}
              isExpanded={expandedIds.has(chunk.recording_session_id)}
              onToggleExpand={() =>
                toggleExpanded(chunk.recording_session_id)
              }
              isDeleteDisabled={isDeleteDisabled}
              isDeleting={isRowDeleting}
              deleteDisabledReason={deleteDisabledReason}
              onDelete={() => handleDeleteClick(chunk)}
              tooltipPopperProps={tooltipPopperProps}
            />
          );
        })}
      </TableBody>
    </Table>
  );
}
